import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import type { LaravelCollection, LaravelResource } from '../types'
import type {
  MyWeek,
  MyWeekEntry,
  MyWeekLookup,
  MyWeekSuggestion,
  MyWeekTarget,
} from '@/models/MyWeek'

/** "current", "next" or an ISO week like "2026-W42". */
export type MyWeekSelector = MyWeekTarget | string

export interface CreateMyWeekEntryPayload {
  task_id: string
  week?: MyWeekTarget
  today?: boolean
  before_entry_id?: string | null
  /** Re-plans an entry that needs the "Bleibt diese Woche" decision in the same request. */
  decided?: true
}

export interface UpdateMyWeekEntryPayload {
  today?: boolean
  week?: MyWeekTarget
  decided?: true
}

export interface ReorderMyWeekPayload {
  week: MyWeekSelector
  today_ids: string[]
  week_ids: string[]
}

export interface UpsertMyWeekPlanPayload {
  week: MyWeekSelector
  days_off?: string[]
  appointment_minutes?: number
  started?: boolean
}

/** GET /api/v2/my-week?week= — the whole screen; default is the current week */
export function getMyWeek(week?: MyWeekSelector | null): Promise<AxiosResponse<LaravelResource<MyWeek>>> {
  return request({
    url: '/api/v2/my-week' + (week ? '?week=' + encodeURIComponent(week) : ''),
    method: 'get',
  })
}

/** GET /api/v2/my-week/suggestions — own open tasks outside the week, ranked, at most 20 */
export function getMyWeekSuggestions(): Promise<AxiosResponse<LaravelCollection<MyWeekSuggestion>>> {
  return request({
    url: '/api/v2/my-week/suggestions',
    method: 'get',
  })
}

/** GET /api/v2/my-week/lookup?task_ids=a,b — which of these tasks are planned (≤ 50 ids) */
export function lookupMyWeekTasks(task_ids: string[]): Promise<AxiosResponse<LaravelCollection<MyWeekLookup>>> {
  return request({
    url: '/api/v2/my-week/lookup?task_ids=' + task_ids.join(','),
    method: 'get',
  })
}

/** POST /api/v2/my-week/entries — 201 new, 200 when the task was planned already (moved) */
export function createMyWeekEntry(
  payload: CreateMyWeekEntryPayload,
): Promise<AxiosResponse<LaravelResource<MyWeekEntry>>> {
  return request({
    url: '/api/v2/my-week/entries',
    method: 'post',
    data: payload,
  })
}

/** PATCH /api/v2/my-week/entries/{id} — 422 DECISION_REQUIRED / NOT_IN_CURRENT_WEEK */
export function updateMyWeekEntry(
  entry_id: string,
  payload: UpdateMyWeekEntryPayload,
): Promise<AxiosResponse<LaravelResource<MyWeekEntry>>> {
  return request({
    url: '/api/v2/my-week/entries/' + entry_id,
    method: 'patch',
    data: payload,
  })
}

/** PATCH /api/v2/my-week/entries/{id}/status — sets only the own assignee status */
export function setMyWeekEntryStatus(
  entry_id: string,
  status: 'closed' | 'open',
): Promise<AxiosResponse<LaravelResource<MyWeekEntry>>> {
  return request({
    url: '/api/v2/my-week/entries/' + entry_id + '/status',
    method: 'patch',
    data: { status },
  })
}

/** PUT /api/v2/my-week/order — answers with the whole week */
export function reorderMyWeek(payload: ReorderMyWeekPayload): Promise<AxiosResponse<LaravelResource<MyWeek>>> {
  return request({
    url: '/api/v2/my-week/order',
    method: 'put',
    data: payload,
  })
}

/** DELETE /api/v2/my-week/entries/{id}[?unassign=1] — 204; unassign needs edit rights (403) */
export function deleteMyWeekEntry(
  entry_id: string,
  options: { unassign?: boolean } = {},
): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v2/my-week/entries/' + entry_id + (options.unassign ? '?unassign=1' : ''),
    method: 'delete',
  })
}

/** POST /api/v2/my-week/entries/{id}/handover — the entry is deleted, I stay a watcher */
export function handoverMyWeekEntry(
  entry_id: string,
  user_id: string,
): Promise<AxiosResponse<Record<string, never>>> {
  return request({
    url: '/api/v2/my-week/entries/' + entry_id + '/handover',
    method: 'post',
    data: { user_id },
  })
}

/** PUT /api/v2/my-week/plan — answers with the whole week */
export function upsertMyWeekPlan(payload: UpsertMyWeekPlanPayload): Promise<AxiosResponse<LaravelResource<MyWeek>>> {
  return request({
    url: '/api/v2/my-week/plan',
    method: 'put',
    data: payload,
  })
}
